import React, {useCallback, useEffect, useState} from 'react';
import {HttpOrders} from "./api/HttpOrders";
import {OrderOption, OrderSummary, OrderViewDTO} from "./types/dto";
import {useConfirmationDialog} from "../../hooks/useConfirmationDialog";
import {usePagination} from "../../hooks/usePagination";
import {z} from "zod";
import {OrderFields, OrderFilterFields} from "./types/fields";
import {zodResolver} from "@hookform/resolvers/zod";
import {useForm} from "react-hook-form";
import {ITableColumn, TableWithPagingAndFilter} from "../../components/table";
import {
    EntityWithIdAndDescription,
    EntityWithIdAndDescriptionFields,
    EntityWithIdFields,
    QueryStatus
} from "../../types";
import Grid from "@mui/material/Grid";
import {AsyncSelect, ControlledTextField} from "../../components/forms";
import Button from "@mui/material/Button";
import SearchIcon from '@mui/icons-material/Search';
import IconButton from "@mui/material/IconButton";
import {ListItemIcon, MenuItem, Stack, TextField, Tooltip} from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import OrderDialog from "./components/OrderDialog";
import {getOrderStatusColor} from "./types/typification";
import OrderStatusChip from "./components/OrderStatusChip";
import {ControlledAutocomplete} from "../../components/forms/ControlledAutocomplete";
import Icon from "../../@core/components/icon";
import OrderStatusChipWithMenu from "./components/OrderStatusChipSelection";
import toast from "react-hot-toast";
import Typography from "@mui/material/Typography";
import {dateFormatter, stringFormatter} from "../../utils/formatters";
import {usePDFGenerator} from "./hooks/usePDFGenerator";
import {OrderStatusTypes} from "./types/enum";
import OrderViewDialog from "./components/OrderViewDialog";
import {ControlledDateRangePicker} from "../../components/forms/ControlledDateRangePicker";
import OrderSummaryCard from "./components/OrderSummaryCard";
import {DumpTruckViewDTO} from "../dump-trucks/types/dto";
import {DumpTruckFields} from "../dump-trucks/types/fields";

const schemaOrderFilter = z.object({
    [OrderFields.Receiver]: z.string().optional(),
    [OrderFields.Address]: z.string().optional(),
    [OrderFields.Items]: z.string().optional(),
    [OrderFilterFields.StatusCodeList]: z.array(z.number()).optional(),
    [OrderFields.OrderDate]: z.date().optional(),
    [OrderFields.DeliveryDate]: z.date().optional(),
    [OrderFilterFields.OrderDateStart]: z.date().optional(),
    [OrderFilterFields.OrderDateEnd]: z.date().optional(),
    [OrderFilterFields.DeliveryDateStart]: z.date().optional(),
    [OrderFilterFields.DeliveryDateEnd]: z.date().optional(),
    [OrderFields.InvoiceNumber]: z.string().optional(),
    [OrderFields.DeliveryNote]: z.string().optional(),
    [OrderFilterFields.IsBilled]: z.preprocess((value) => {
        if (value === -1) {
            return null;
        }
        if (value === 1) {
            return true;
        }
        if (value === 2) {
            return false;
        }

        return value;
    }, z.boolean().optional().nullable()),
})

export type OrderFilterType = z.infer<typeof schemaOrderFilter>

function Orders() {
    const generatePDF = usePDFGenerator();

    const [showFilter, setShowFilter] = useState<boolean>(false)
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogType, setDialogType] = useState<'view' | 'edit'>('view')
    const [selectedOrder, setSelectedOrder] = useState<OrderViewDTO>()
    const [orderOptionList, setOrderOptionList] = useState<OrderOption[]>([]);
    const [orderList, setOrderList] = useState<OrderViewDTO[]>([]);
    const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
    const [summary, setSummary] = useState<OrderSummary>();

    const {askConfirmation} = useConfirmationDialog()

    const onSearch = (data: OrderFilterType) => {
        setFilterAndSearch(data)
    }

    const {control, getValues, handleSubmit} = useForm<OrderFilterType>({
        defaultValues: {
            [OrderFilterFields.StatusCodeList]: [OrderStatusTypes.PENDING],
            [OrderFields.Receiver]: undefined,
        },
        resolver: zodResolver(schemaOrderFilter)
    })

    const {
        entities: orders,
        status: ordersStatus,
        paginacion: pagination,
        onChange,
        refetch,
        setFilterAndSearch
    } = usePagination<OrderViewDTO, OrderFilterType>({
        initialFilter: {...getValues()},
        loader: (pagination, filter) => HttpOrders.get(pagination, filter)
    })

    useEffect(() => {
        setOrderList(orders)
    }, [orders])


    const columns: ITableColumn[] = [
        {
            label: "Destinatario",
            width: '20%',
            onRenderCell: (order: OrderViewDTO) => (
                <Stack direction={'column'}>
                    <Typography variant='body1' noWrap sx={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {order[OrderFields.Receiver]}
                    </Typography>
                    <Typography variant='caption' noWrap sx={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {order[OrderFields.Address]}
                    </Typography>
                </Stack>
            ),
        },

        {
            label: "Mercaderia",
            width: '25%',
            onRenderCell: (order: OrderViewDTO) => {
                return (
                    <Tooltip title={order[OrderFields.Items] || ''}>
                        <Typography
                            variant={'body2'}
                            sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2, // para que muestre hasta 2 lineas
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: '300px',
                            }}
                        >
                            {order[OrderFields.Items]}
                        </Typography>
                    </Tooltip>
                );
            },
        },

        {
            label: "Devoluciones", onRenderCell: (order: OrderViewDTO) => {
                const maxLength = 30

                return (
                    <Tooltip
                        title={(order?.[OrderFields.Observations]?.length || 0) > maxLength ? order[OrderFields.Observations] : ''}>
                        <Typography variant={'body2'} noWrap>
                            {stringFormatter.cutIfHaveMoreThan(order[OrderFields.Observations], maxLength)}
                        </Typography>
                    </Tooltip>
                )
            }, width: '10%',
        },

        {
            label: "Fechas",
            onRenderCell: (row: OrderViewDTO) => (
                <Stack>
                    <Typography variant='body2' sx={{fontSize: '0.75rem'}}>
                        <b>Alta:</b> {row[OrderFields.OrderDate] ? dateFormatter.toShortDate(row[OrderFields.OrderDate]) : '-'}
                    </Typography>
                    <Typography variant='body2' sx={{fontSize: '0.75rem'}}>
                        <b>Entrega:</b> {row[OrderFields.DeliveryDate] ? dateFormatter.toShortDate(row[OrderFields.DeliveryDate]) : '-'}
                    </Typography>
                </Stack>
            ),
            width: '20%',
        },
        {
            label: "Remito / Factura",
            width: '15%',
            onRenderCell: (row: OrderViewDTO) => (
                <Stack>
                    <Typography variant='body2'>
                        {row[OrderFields.DeliveryNote] || 'Sin Remito'}
                    </Typography>
                    <Typography variant='body2'>
                        {row[OrderFields.InvoiceNumber] || 'Sin Factura'}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: "Estado",
            width: '10%',
            onRenderCell: (order: OrderViewDTO) => {
                return (
                    <OrderStatusChipWithMenu
                        orderOption={order}
                        orderOptionList={orderOptionList}
                        onOptionChange={(newOption) => {
                            const newOrder = {...order, [OrderFields.StatusCode]: newOption[OrderFields.StatusCode]}

                            return askConfirmation(
                                () => {
                                    const promise = HttpOrders.put(order[EntityWithIdFields.Id], newOrder)

                                    toast.promise(promise, {
                                        success: 'Actualizado',
                                        error: 'No se pudo actualizar',
                                        loading: 'Actualizando'
                                    }).then(() => refetch())
                                }, `¿Desea actualizar el estado a: ${newOption[OrderFields.StatusDesc]}?`,
                                <TextField
                                    sx={{mt: 2, width: 1}}
                                    label={'Devoluciones'}
                                    value={newOrder[OrderFields.Observations]}
                                    onChange={(event => newOrder[OrderFields.Observations] = event.target.value)}
                                    rows={8} multiline fullWidth/>
                            )
                        }}
                    />
                )
            }
        },
        {
            label: "Acciones",
            width: '10%',
            onRenderCell: (order: OrderViewDTO) => {
                return (
                    <Stack direction={'row'}>
                        <Tooltip title='Ver'>
                            <IconButton onClick={() => {
                                setSelectedOrder(order)
                                setDialogType('edit')
                                setDialogOpen(true)
                            }}>
                                <SearchIcon color='primary'/>
                            </IconButton>
                        </Tooltip>

                        {
                            order[OrderFields.StatusCode] === OrderStatusTypes.PENDING
                            &&
                            <>
                                <IconButton
                                    color='primary'
                                    disabled={orderList.indexOf(order) === 0}
                                    onClick={() => {
                                        const promise = updateDisplayOrder(order, -1)

                                        toast.promise(promise, {
                                            success: 'Actualizado',
                                            error: 'No se pudo actualizar',
                                            loading: 'Actualizando'
                                        }).catch(() => refetch())
                                    }}>
                                    <ArrowUpwardIcon/>
                                </IconButton>
                                <IconButton
                                    disabled={orderList.indexOf(order) === orderList.length - 1}
                                    color='primary'
                                    onClick={() => {
                                        const promise = updateDisplayOrder(order, +1)

                                        toast.promise(promise, {
                                            success: 'Actualizado',
                                            error: 'No se pudo actualizar',
                                            loading: 'Actualizando'
                                        }).catch(() => refetch())
                                    }}>
                                    <ArrowDownwardIcon/>
                                </IconButton>
                            </>
                        }

                    </Stack>

                )

            }
        }
    ]

    const updateDisplayOrder = useCallback((order: OrderViewDTO, orderShiftValue: number) => {
        const orderIndex = orderList.findIndex(o => o[EntityWithIdFields.Id] === order[EntityWithIdFields.Id]);
        if (orderIndex !== -1 && orderIndex + orderShiftValue >= 0 && orderIndex + orderShiftValue < orderList.length) {
            const newOrders = orderList.map(o => ({...o}));
            const targetIndex = orderIndex + orderShiftValue;
            const newDisplayOrder = newOrders[orderIndex][OrderFields.DisplayOrder] + orderShiftValue
            newOrders[orderIndex] = {
                ...newOrders[orderIndex],
                [OrderFields.DisplayOrder]: newDisplayOrder
            };
            newOrders[targetIndex] = {
                ...newOrders[targetIndex],
                [OrderFields.DisplayOrder]: newOrders[targetIndex][OrderFields.DisplayOrder] - orderShiftValue
            };
            const sortedOrders = newOrders.sort((a, b) => a[OrderFields.DisplayOrder] - b[OrderFields.DisplayOrder]);
            setOrderList(sortedOrders);

            return HttpOrders.updateOrderDisplayOrder(order[EntityWithIdFields.Id], newDisplayOrder)
        } else return Promise.reject()
    }, [orderList]);

    useEffect(() => {
        HttpOrders.getOrderStateTypes().then(data => {
            setOrderOptionList(data.map(d => ({
                [OrderFields.StatusCode]: d[EntityWithIdAndDescriptionFields.Id],
                [OrderFields.StatusDesc]: d[EntityWithIdAndDescriptionFields.Description]
            })))
        })
    }, [])

    useEffect(() => {
        HttpOrders.getSummary().then(setSummary).finally(() => setLoadingSummary(false));
    }, []);

    return (
        <>

            {
                !(loadingSummary || summary === undefined) &&
                <OrderSummaryCard summary={summary}/>
            }

            <TableWithPagingAndFilter
                title='Administración de Entregas'

                entityPaginada={orderList && pagination ? {lista: orderList, paginacion: pagination} : undefined}
                columns={columns}

                error={ordersStatus === QueryStatus.Error}
                isLoading={ordersStatus === QueryStatus.Loading || ordersStatus === QueryStatus.Idle}
                onShowChildren={() => setShowFilter(!showFilter)}
                showChildren={showFilter}
                childrenName={"Filtros"}
                onPaging={onChange}
                onNewRegister={() => {
                    setDialogType('edit')
                    setDialogOpen(true)
                }}
                selectable={true}
                handleSelectedAction={(selected) => {
                    const selectedOrders = orderList.filter(order => selected.includes(order.id));
                    generatePDF(selectedOrders)

                }}
                selectedActionName={'Descargar PDF'}
                selectedActionIcon={<Icon icon={'material-symbols:download'}/>}
            >

                <Grid container spacing={4} sx={{p: 5}}>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control}
                                             name={OrderFields.Receiver}
                                             label={'Destinatario'}
                                             fullWidth
                        />
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control}
                                             name={OrderFields.Address}
                                             label={'Dirección'}
                                             fullWidth
                        />
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control}
                                             name={OrderFields.Items}
                                             label={'Mercadería'}
                                             fullWidth
                        />
                    </Grid>


                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control} name={OrderFields.DeliveryNote}
                                             label={'Nro. Remito'} fullWidth/>
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control} name={OrderFields.InvoiceNumber}
                                             label={'Nro. Factura'} fullWidth/>
                    </Grid>
                    <Grid item md={4} sm={6} xs={12}>
                        <AsyncSelect
                            name={OrderFilterFields.IsBilled}
                            control={control}
                            label="Facturado"

                            // en el schema se hace trampa para convertir estos valores a true false o null
                            loadOptions={() => Promise.resolve([
                                {id: 1, descripcion: 'Sí'},
                                {id: 2, descripcion: 'No'},
                                {id: -1, descripcion: 'Todos'},
                            ])}
                            valueField="id"
                            selectNotNulleable
                        />
                    </Grid>


                    <Grid item md={5} sm={6} xs={12}>
                        <ControlledAutocomplete loadOptions={HttpOrders.getOrderStateTypes}
                                                control={control}
                                                name={OrderFilterFields.StatusCodeList}
                                                label={'Estado'}
                                                valueField={'id'}
                                                multiple
                                                TagComponent={(value: EntityWithIdAndDescription) => {
                                                    const option: OrderOption = {
                                                        [OrderFields.StatusCode]: value[EntityWithIdAndDescriptionFields.Id],
                                                        [OrderFields.StatusDesc]: value[EntityWithIdAndDescriptionFields.Description]
                                                    }

                                                    return (
                                                        (props: any) =>
                                                            <OrderStatusChip orderOption={option} {...props}/>
                                                    )
                                                }}
                                                renderOption={(renderProps, value: EntityWithIdAndDescription, state) => {
                                                    const color = getOrderStatusColor(value[EntityWithIdAndDescriptionFields.Id])

                                                    return (
                                                        <MenuItem key={state.index} selected={state.selected}
                                                                  value={value.id} {...renderProps}>
                                                            <ListItemIcon>
                                                                <Icon
                                                                    icon='mdi:circle'
                                                                    color={color}
                                                                    fontSize='1rem'
                                                                />
                                                            </ListItemIcon>
                                                            {value.descripcion}
                                                        </MenuItem>
                                                    )
                                                }}
                        />

                    </Grid>
                    <Grid item md={3.5} sm={12} xs={12}>
                        <ControlledDateRangePicker
                            control={control}
                            label={'Fecha alta'}
                            startDateName={OrderFilterFields.OrderDateStart}
                            endDateName={OrderFilterFields.OrderDateEnd}
                        />

                    </Grid>

                    <Grid item md={3.5} sm={12} xs={12}>
                        <ControlledDateRangePicker
                            control={control}
                            label={'Fecha de Entrega'}
                            startDateName={OrderFilterFields.DeliveryDateStart}
                            endDateName={OrderFilterFields.DeliveryDateEnd}
                        />
                    </Grid>

                    <Grid item xs={12} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Button
                            variant='contained'
                            onClick={handleSubmit(onSearch)}
                            startIcon={<SearchIcon/>}
                        >
                            Buscar
                        </Button>
                    </Grid>

                </Grid>
            </TableWithPagingAndFilter>
            {
                dialogOpen &&
                <OrderDialog open={dialogOpen}
                             onClose={() => {
                                 setSelectedOrder(undefined)
                                 setDialogOpen(false)
                             }}
                             onAfterSubmit={() => {
                                 refetch()
                                 setSelectedOrder(undefined)
                                 setDialogOpen(false)
                             }}
                             order={selectedOrder}
                             maxWidth={'md'}
                />
            }

        </>
    );
}

export default Orders;